import { ChevronDown, ChevronRight } from "lucide-react";

import { Checkbox } from "components/ui/Checkbox/Checkbox";
import { MultiSelect } from "components/ui/MultiSelect/MultiSelect";
import {
  privateInsuranceProviders,
  selectableFinancialClasses,
} from "utils/patientFormOptions";

import {
  CDM_FINDER_ATC_CLASSES,
  CDM_FINDER_ATHEROSCLEROTIC_TEXT,
  CDM_FINDER_CONDITIONS_BY_TYPE,
  CDM_FINDER_GENERAL_TEXT,
  CDM_FINDER_GROUP_OPTIONS,
  CDM_FINDER_REVIEW_TYPES,
  getFinderAgeBandsForReviewType,
  getFinderPreset,
  getFinderReviewTypeInfo,
} from "../../constants/cdmFinder";
import { CdmConditionSelect } from "../CdmConditionSelect/CdmConditionSelect";
import {
  Field,
  FinderSelectField,
  GroupedFinderSelectField,
  REPORT_GENDER_OPTIONS,
  TextInput,
} from "./fields";
import styles from "./ReportsFilters.module.css";
import { useAdvancedToggle } from "./useAdvancedToggle";

// Selection 3/4 (spec p.2). The spec only defines two states, On / Not on; the
// finder's whole purpose is to surface patients NOT yet on the programme, so
// "Not on programme" is the default (a missing value is treated as "not" too).
const PROGRAMME_STATUS_OPTIONS = [
  { value: "not", label: "Not on programme" },
  { value: "on", label: "On programme" },
];

const PROGRAMME_STATUS_DEFAULT = "not";

// Programme select: CDM / PP / OCF, plus the two CKD finders kept as their own
// separate entries. A CKD entry routes to a programme by eGFR band (Treatment
// 0–29, Prevention 30–59); its reviewType collapses to "CKD".
const CKD_TREATMENT = "CKD_TREATMENT";
const CKD_PREVENTION = "CKD_PREVENTION";

const PROGRAMME_OPTIONS = [
  { value: "", label: "All programmes" },
  ...CDM_FINDER_REVIEW_TYPES,
  { value: CKD_TREATMENT, label: "CKD Finder – Treatment" },
  { value: CKD_PREVENTION, label: "CKD Finder – Prevention" },
];

const CKD_EGFR_DEFAULTS = {
  [CKD_TREATMENT]: { minEgfr: 0, maxEgfr: 29 },
  [CKD_PREVENTION]: { minEgfr: 30, maxEgfr: 59 },
};

export const CdmFinderFilters = ({
  filters,
  onFilterChange,
  useGroupedFinder = false,
}) => {
  const hasMedicationCriteria = (filters.atcCodes?.length ?? 0) > 0;
  const longTermRepeatOnly = filters.longTermRepeatOnly ?? true;

  const programme = filters.finderProgramme || "";
  const reviewType = filters.finderReviewType || "";
  const isCkd = reviewType === "CKD";
  const ageBandOptions = getFinderAgeBandsForReviewType(reviewType);

  // Only offer conditions that map to at least one ICD-10 code (the finder
  // matches on ICD-10 / ATC only). CKD has no condition select — it uses the
  // eGFR range instead. Multi-select: no "Any" entry (empty = whole programme).
  const conditionOptions =
    reviewType && !isCkd
      ? (CDM_FINDER_CONDITIONS_BY_TYPE[reviewType] || [])
          .filter((condition) => (condition.icd10Codes?.length ?? 0) > 0)
          .map(({ value, label, icd10Codes }) => ({
            value,
            label: `${label} (${icd10Codes.map((code) => code.code).join(", ")})`,
          }))
      : [];

  // Picking a programme seeds the reviewType + condition. The two CKD entries
  // collapse to reviewType "CKD" and pre-seed their eGFR band; age band is reset
  // when the new programme doesn't offer the current one.
  const handleProgrammeChange = (value) => {
    const ckdDefaults = CKD_EGFR_DEFAULTS[value];
    const nextReviewType = ckdDefaults ? "CKD" : value;

    onFilterChange("finderProgramme", value);
    onFilterChange("finderReviewType", nextReviewType);
    onFilterChange("finderConditions", ckdDefaults ? ["CKD"] : []);
    onFilterChange("minEgfr", ckdDefaults ? ckdDefaults.minEgfr : "");
    onFilterChange("maxEgfr", ckdDefaults ? ckdDefaults.maxEgfr : "");

    const nextAgeBands = getFinderAgeBandsForReviewType(nextReviewType);
    const stillValid = nextAgeBands.some(
      (band) => band.value === filters.finderAgeBand
    );
    if (!stillValid) onFilterChange("finderAgeBand", "");
  };

  // Grouped picker (spec p.18): seeds reviewType + condition (+ eGFR for CKD).
  const handleFinderChange = (key) => {
    const preset = getFinderPreset(key);
    onFilterChange("finderKey", key);
    onFilterChange("finderProgramme", "");
    onFilterChange("finderReviewType", preset?.reviewType || "");
    onFilterChange(
      "finderConditions",
      preset?.condition ? [preset.condition] : []
    );
    onFilterChange("minEgfr", preset?.egfrMin ?? "");
    onFilterChange("maxEgfr", preset?.egfrMax ?? "");

    const nextAgeBands = getFinderAgeBandsForReviewType(preset?.reviewType || "");
    const stillValid = nextAgeBands.some(
      (band) => band.value === filters.finderAgeBand
    );
    if (!stillValid) onFilterChange("finderAgeBand", "");
  };

  const { showAdvanced, toggleAdvanced } = useAdvancedToggle();
  const advancedActiveCount = [
    filters.patientFirstname,
    filters.patientLastname,
    filters.gender,
    filters.finderAgeBand,
    filters.financialClass?.length ? "set" : "",
    filters.minSystolic,
    filters.minDiastolic,
    filters.minBmi,
    filters.insurerNames?.length ? "set" : "",
    filters.currentSmoker ? "set" : "",
    filters.heartFailureMedCombo ? "set" : "",
    filters.probableFirst ? "set" : "",
    filters.onTreatmentProgramme === "on" ? "set" : "",
    filters.onPreventionProgramme === "on" ? "set" : "",
  ].filter(Boolean).length;

  return (
    <div className={styles.finderLayout}>
      <div
        className={`${styles.finderGridPrimary} ${
          useGroupedFinder && !isCkd ? styles.finderGridPrimaryGrouped : ""
        }`}
      >
        {useGroupedFinder ? (
          <GroupedFinderSelectField
            label="Finder"
            groups={CDM_FINDER_GROUP_OPTIONS}
            value={filters.finderKey || ""}
            onChange={handleFinderChange}
            placeholder="Select a finder"
          />
        ) : (
          <>
            <FinderSelectField
              label="Programme"
              options={PROGRAMME_OPTIONS}
              value={programme}
              onChange={handleProgrammeChange}
            />
            {!isCkd && (
              <Field label="Conditions">
                <MultiSelect
                  options={conditionOptions}
                  value={filters.finderConditions || []}
                  onChange={(value) =>
                    onFilterChange("finderConditions", value)
                  }
                  placeholder="Select condition(s)"
                  disabled={!reviewType}
                />
              </Field>
            )}
          </>
        )}
        {!!isCkd && (
          <Field label="eGFR range (ml/min)">
            <div className={styles.finderBpInputs}>
              <input
                type="number"
                min="0"
                className={styles.finderInput}
                value={filters.minEgfr ?? ""}
                onChange={(event) =>
                  onFilterChange("minEgfr", event.target.value)
                }
                placeholder="Min"
                aria-label="Minimum eGFR"
              />
              <span className={styles.finderBpSep}>–</span>
              <input
                type="number"
                min="0"
                className={styles.finderInput}
                value={filters.maxEgfr ?? ""}
                onChange={(event) =>
                  onFilterChange("maxEgfr", event.target.value)
                }
                placeholder="Max"
                aria-label="Maximum eGFR"
              />
            </div>
          </Field>
        )}
        <Field label="Additional conditions">
          <CdmConditionSelect
            value={filters.icd10Codes || []}
            onChange={(value) => onFilterChange("icd10Codes", value)}
          />
        </Field>
        <Field label="Medication class (ATC)">
          <MultiSelect
            options={CDM_FINDER_ATC_CLASSES}
            value={filters.atcCodes || []}
            onChange={(value) => onFilterChange("atcCodes", value)}
            placeholder="Select medication classes"
          />
        </Field>
      </div>

      {!!hasMedicationCriteria && (
        <div
          className={styles.finderToggle}
          onClick={() =>
            onFilterChange("longTermRepeatOnly", !longTermRepeatOnly)
          }
        >
          <Checkbox checked={longTermRepeatOnly} onChange={() => {}} />
          <span>Long-term / repeat scripts only</span>
        </div>
      )}

      <div className={styles.finderAdvancedSection}>
        <button
          type="button"
          className={styles.finderAdvancedToggle}
          onClick={toggleAdvanced}
          aria-expanded={showAdvanced}
        >
          {showAdvanced ? (
            <ChevronDown size={16} aria-hidden />
          ) : (
            <ChevronRight size={16} aria-hidden />
          )}
          <span>Advanced filters</span>
          {!showAdvanced && advancedActiveCount > 0 && (
            <span className={styles.finderAdvancedBadge}>
              {advancedActiveCount}
            </span>
          )}
        </button>

        {!!showAdvanced && (
          <div className={styles.finderAdvanced}>
            <div className={styles.finderGrid}>
              <Field label="First Name">
                <TextInput
                  value={filters.patientFirstname}
                  onChange={(value) =>
                    onFilterChange("patientFirstname", value)
                  }
                  placeholder="First name"
                  className={styles.finderInput}
                />
              </Field>
              <Field label="Last Name">
                <TextInput
                  value={filters.patientLastname}
                  onChange={(value) => onFilterChange("patientLastname", value)}
                  placeholder="Last name"
                  className={styles.finderInput}
                />
              </Field>
              <FinderSelectField
                label="Gender"
                options={REPORT_GENDER_OPTIONS}
                value={filters.gender}
                onChange={(value) => onFilterChange("gender", value)}
              />
              <FinderSelectField
                label="Age band"
                options={ageBandOptions}
                value={filters.finderAgeBand}
                onChange={(value) => onFilterChange("finderAgeBand", value)}
              />
              <Field label="Scheme">
                <MultiSelect
                  options={selectableFinancialClasses}
                  value={filters.financialClass || []}
                  onChange={(value) => onFilterChange("financialClass", value)}
                  placeholder="Select scheme(s)"
                />
              </Field>
              <FinderSelectField
                label="Treatment Programme"
                options={PROGRAMME_STATUS_OPTIONS}
                value={filters.onTreatmentProgramme || PROGRAMME_STATUS_DEFAULT}
                onChange={(value) =>
                  onFilterChange("onTreatmentProgramme", value)
                }
              />
              <FinderSelectField
                label="Prevention Programme"
                options={PROGRAMME_STATUS_OPTIONS}
                value={filters.onPreventionProgramme || PROGRAMME_STATUS_DEFAULT}
                onChange={(value) =>
                  onFilterChange("onPreventionProgramme", value)
                }
              />
              <Field label="Blood pressure (min)">
                <div className={styles.finderBpInputs}>
                  <input
                    type="number"
                    min="0"
                    className={styles.finderInput}
                    value={filters.minSystolic || ""}
                    onChange={(event) =>
                      onFilterChange("minSystolic", event.target.value)
                    }
                    placeholder="Sys"
                    aria-label="Minimum systolic"
                  />
                  <span className={styles.finderBpSep}>/</span>
                  <input
                    type="number"
                    min="0"
                    className={styles.finderInput}
                    value={filters.minDiastolic || ""}
                    onChange={(event) =>
                      onFilterChange("minDiastolic", event.target.value)
                    }
                    placeholder="Dia"
                    aria-label="Minimum diastolic"
                  />
                </div>
              </Field>
              <Field label="Min BMI">
                <input
                  type="number"
                  min="0"
                  step="0.1"
                  className={styles.finderInput}
                  value={filters.minBmi || ""}
                  onChange={(event) =>
                    onFilterChange("minBmi", event.target.value)
                  }
                  placeholder="e.g. 30"
                />
              </Field>
              <Field label="Private insurer">
                <MultiSelect
                  options={privateInsuranceProviders}
                  value={filters.insurerNames || []}
                  onChange={(value) => onFilterChange("insurerNames", value)}
                  placeholder="Select insurer(s)"
                />
              </Field>
            </div>

            <div className={styles.finderChecks}>
              <div
                className={styles.finderToggle}
                onClick={() =>
                  onFilterChange("currentSmoker", !filters.currentSmoker)
                }
              >
                <Checkbox
                  checked={!!filters.currentSmoker}
                  onChange={() => {}}
                />
                <span>Current smoker</span>
              </div>
              <div
                className={styles.finderToggle}
                onClick={() =>
                  onFilterChange(
                    "heartFailureMedCombo",
                    !filters.heartFailureMedCombo
                  )
                }
              >
                <Checkbox
                  checked={!!filters.heartFailureMedCombo}
                  onChange={() => {}}
                />
                <span>Heart failure medication combination</span>
              </div>
              <div
                className={styles.finderToggle}
                onClick={() =>
                  onFilterChange("probableFirst", !filters.probableFirst)
                }
              >
                <Checkbox
                  checked={!!filters.probableFirst}
                  onChange={() => {}}
                />
                <span>Probable cases first</span>
              </div>
            </div>
          </div>
        )}
      </div>

      <div className={styles.finderNote}>
        <p style={{ margin: 0 }}>{getFinderReviewTypeInfo(reviewType)}</p>
        {(filters.finderConditions || []).includes("ATHEROSCLEROTIC") && (
          <p style={{ margin: "8px 0 0" }}>{CDM_FINDER_ATHEROSCLEROTIC_TEXT}</p>
        )}
        <p style={{ margin: "8px 0 0" }}>{CDM_FINDER_GENERAL_TEXT}</p>
      </div>
    </div>
  );
};
